import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Minus, X, Info, Loader2, Send } from "lucide-react";
import FixedLayer from "./FixedLayer";

const BASE = "https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com";

const deviceId = (() => {
  const k = "teche_device_id";
  let id = localStorage.getItem(k);
  if (!id) { id = (crypto.randomUUID?.() || (Date.now()+"-"+Math.random())); localStorage.setItem(k, id); }
  return id;
})();

export default function DMPage() {
  const { convId } = useParams();
  const navigate = useNavigate();
  const me = useMemo(() => { try { return JSON.parse(localStorage.getItem("user")); } catch { return null; } }, []);
  const [meta, setMeta] = useState({ name: "Chat", avatar: "/avatar.png", lastReadAt: 0 }); // {name, avatar, lastReadAt}
  const [msgs, setMsgs] = useState([]);
  const [before, setBefore] = useState(null);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const [showIntro, setShowIntro] = useState(() => !localStorage.getItem(`dm:intro:hidden:${convId}`));
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const scrollRef = useRef(null);
  const [showBubble, setShowBubble] = useState(true);
  const panelRef = useRef(null);

  // tell ChatFloat to close + clear bubbles
  useEffect(() => { window.dispatchEvent(new Event("chatfloat:close")); }, []);


  // helpers
  const groupByDay = (list) => {
    const fmt = (d) => new Date(+d).toLocaleDateString([], { year:"numeric", month:"short", day:"numeric" });
    const map = [];
    let cur = null, curKey = null;
    list.forEach(m => {
      const k = fmt(m.createdAt);
      if (k !== curKey) { curKey = k; cur = { key:k, items:[] }; map.push(cur); }
      cur.items.push(m);
    });
    return map;
  };

  const loadMeta = async () => {
    // We don’t have a dedicated endpoint, so read from conversations_list and pick this conv
    if (!me?.email) return;
    const url = new URL(`${BASE}/conversations_list`);
    url.searchParams.set("email", me.email);
    const data = await fetch(url).then(r=>r.json()).catch(()=>({items:[]}));
    const row = (data.items || []).find(x => x.id === convId);
    if (row) setMeta({
      name: row.name || "Chat",
      avatar: row.avatar || "/avatar.png",
      lastReadAt: row.lastReadAt || 0,
    });
    // open a chat-head for this DM
    window.dispatchEvent(new CustomEvent("dm:open", {
    detail: { convId, name: row.name, avatar: row.avatar }
    }));
  };

  const loadMessages = async (olderThan=null) => {
    const url = new URL(`${BASE}/messages_page`);
    url.searchParams.set("convId", convId);
    if (olderThan) url.searchParams.set("before", olderThan);
    const res = await fetch(url);
    const data = await res.json();
    // API returns newest→oldest; we reverse to render chronological
    const arr = data.items.reverse();
    setMsgs(prev => olderThan ? [...arr, ...prev] : arr);
    setBefore(arr.length ? arr[0].createdAt : null);
    // mark read
    if (!olderThan && me?.email) {
      fetch(`${BASE}/messages_read?convId=${encodeURIComponent(convId)}&email=${encodeURIComponent(me.email)}&upToCreatedAt=${Date.now()}`).catch(()=>{});
    }
  };

  const send = async () => {
    const t = text.trim();
    if (!t || !me?.email) return;
    setSending(true);
    setText("");

    const dedupeKey = `${deviceId}:${Date.now()}`;
    const optimistic = { id: dedupeKey, convId, senderEmail: me.email, text: t, createdAt: Date.now() };
    setMsgs(prev => [...prev, optimistic]);
    try {
      const body = new URLSearchParams({ convId, senderEmail: me.email, text: t, dedupeKey });
      const res = await fetch(`${BASE}/messages_send`, { method:"POST", headers:{ "Content-Type":"application/x-www-form-urlencoded" }, body });
      const data = await res.json();
      setMsgs(prev => prev.map(m => m.id===dedupeKey ? data.msg : m));
    } catch {
      setMsgs(prev => prev.map(m => m.id===dedupeKey ? { ...m, failed:true } : m));
    } finally {
      setSending(false);
    }
  };

  // effects
  useEffect(() => { if (showIntro === false) localStorage.setItem(`dm:intro:hidden:${convId}`, "1"); }, [showIntro, convId]);

  useEffect(() => { loadMeta(); loadMessages(null); }, [convId, me?.email]);
  
  // long-poll
  useEffect(() => {
    let aborted = false;
    let since = Date.now();
    const tick = async () => {
      try {
        const res = await fetch(`${BASE}/live_events?convId=${convId}&since=${since}`, { signal: AbortSignal.timeout ? AbortSignal.timeout(25000) : undefined });
        if (!res.ok) throw new Error();
        const ev = await res.json();
        since = ev.now || Date.now();
        if (ev.newMessages?.length) setMsgs(prev => [...prev, ...ev.newMessages]);
      } catch {}
      if (!aborted) setTimeout(tick, 500);
    };
    tick();
    return () => { aborted = true; };
  }, [convId]);

  // scroll to bottom when new messages
  // Should always show the bottom of the conversation to show last messages
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs.length]);

  


  // render
  const buckets = groupByDay(msgs);

  return (
  <FixedLayer>
    
<div
  style={{
    position: "fixed",
    left: 20,
    top: 20,
    zIndex: 2147483647,
    padding: "6px 10px",
    background: "rgba(255,0,0,0.85)",
    color: "#fff",
    fontSize: 12,
    borderRadius: 6,
  }}
>
  DMPage mounted
</div>

    {showBubble && (
      // Conversation bubble — same offset as ChatFloat
      <button
        onClick={() => setShowBubble(false)}   // hide when clicking the bubble
        className="fixed z-[2147483647] w-12 h-12 rounded-full border bg-white shadow-lg overflow-hidden ring-2 ring-white"
        style={{ position: "fixed", left: 16, bottom: 16, right: "auto" }}
        title={meta.name || "Chat"}
        aria-label="Open conversation"
      >
        <img src={meta.avatar || "/avatar.png"} alt="" className="w-full h-full object-cover" />
      </button>
    )}


    {/* DM panel */}
    <div 
      ref={panelRef}
      className="fixed z-[2147483646] w-[min(420px,92vw)] max-h-[74vh] bg-white rounded-2xl shadow-2xl border flex flex-col"
      style={{ position: "fixed", left: 16, bottom: 90, right: "auto" }}
    >
      {/* Header */}
      <header className="flex items-center justify-between px-3 py-2 border-b">
        <div className="flex items-center gap-2 min-w-0">
          <img src={meta.avatar || "/avatar.png"} alt="" className="w-8 h-8 rounded-full object-cover" />
          <div className="truncate font-semibold">{meta.name || "Chat"}</div>
        </div>
        <div className="flex items-center gap-2">
          <button title="Minimize" className="p-1 rounded hover:bg-gray-100" onClick={() => navigate("/messages")}>
            <Minus className="w-4 h-4" />
          </button>
          <button title="Close" className="p-1 rounded hover:bg-gray-100" onClick={() => navigate(-1)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Body */}
      <div ref={scrollRef} className="flex-1 overflow-auto px-3 py-3">
        {showIntro && (
          <div className="mx-auto my-4 max-w-[85%] text-center">
            <img src={meta.avatar || "/avatar.png"} alt="" className="w-16 h-16 rounded-full object-cover mx-auto" />
            <h3 className="mt-3 font-semibold text-gray-900">{meta.name || "Chat"}</h3>
            <p className="mt-2 text-xs text-gray-600 flex items-start gap-1 justify-center">
              <span className="inline-flex mt-0.5"><Info className="w-3.5 h-3.5 text-gray-500" /></span>
              <span>Messages on Teche are secured with end-to-end encryption. Only people in this chat can read, listen to, or share them.</span>
            </p>
            <button onClick={() => setLearnMoreOpen(true)} className="mt-1 text-xs text-[#324B8E] hover:underline">
              Learn more
            </button>
            <button onClick={() => setShowIntro(false)} className="mt-3 text-xs px-3 py-1 rounded-full border text-gray-700 hover:bg-gray-50">
              OK
            </button>
          </div>
        )}

        {groupByDay(msgs).map(b => (
          <div key={b.key}>
            <div className="my-2 text-[11px] text-gray-500 text-center">{b.key}</div>
            {b.items.map(m => {
              const mine = m.senderEmail?.toLowerCase() === me?.email?.toLowerCase();
              return (
                <div key={m.id} className={"mb-2 flex " + (mine ? "justify-end" : "justify-start")}>
                  <div className={"max-w-[80%] rounded-2xl px-3 py-2 text-sm " + (mine ? "bg-[#324B8E] text-white" : "bg-gray-100 text-gray-900")}>
                    <div className="whitespace-pre-wrap break-words">
                      {m.text?.length > 260
                        ? (<details><summary className="cursor-pointer">Read more</summary>{m.text}</details>)
                        : m.text}
                    </div>
                    <div className="text-[10px] opacity-70 mt-1">
                      {new Date(Number(m.createdAt)).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {!msgs.length && !showIntro && (
          <div className="h-24 flex items-center justify-center text-sm text-gray-500">No messages yet.</div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t p-2 flex items-center gap-2">
        <input
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Write a message…"
          className="flex-1 border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-300"
        />
        <button onClick={send} disabled={sending || !text.trim()} className="p-2 rounded-xl bg-[#324B8E] text-white disabled:opacity-50">
          {sending ? <Loader2 className="animate-spin w-4 h-4" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>

    {/* Learn more modal */}
      {learnMoreOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[10000]">
          <div className="w-[min(520px,92vw)] bg-white rounded-2xl shadow-2xl border p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold">End-to-end encryption and your privacy</h3>
              <button className="p-1 rounded hover:bg-gray-100" onClick={() => setLearnMoreOpen(false)} aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <div className="font-medium">How it works</div>
                <p className="mt-1">Messages have an extra layer of security as they travel between you and the person you’re talking to.</p>
              </div>
              <div>
                <div className="font-medium">What to keep in mind</div>
                <p className="mt-1">You can still report messages in end-to-end encrypted chats. Learn more.</p>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button className="px-3 py-1.5 rounded-lg border text-sm" onClick={() => setLearnMoreOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

  </FixedLayer>
);
}
